import { memo, useCallback } from 'react';

import { CloseFullScreen, OpenInFull } from 'icons';
import { isKeyHotkey } from 'is-hotkey';
import { useIntl } from 'localization';
import { BaseEditor, Editor, Element, Node, NodeEntry, Path, Range, Transforms } from 'slate';
import { HistoryEditor } from 'slate-history';
import { Editable, ReactEditor, RenderElementProps, RenderLeafProps, Slate } from 'slate-react';
import { twMerge } from 'tailwind-merge';

import Leaf from './Leaf';
import LongTextWrap from './LongTextWrap';
import RenderElement from './RenderElement';
import { hotKeys } from './constants';
import { useMentions } from './hooks/useMentions';
import Toolbar from './toolbar/Toolbar';
import { CustomElement, CustomText, Format, MentionOption } from './types';
import { decorate, handleTextReplacement, toggleFormat } from './utils';

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

export type ShortCuts =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'link'
  | 'emoji'
  | 'image'
  | 'numbered-list'
  | 'bulleted-list'
  | 'columns'
  | 'code';

type RichTextEditorProps = {
  onBlur?: (value: { textJson: CustomElement[] }) => void;
  placeholder?: string;
  actionElement?: JSX.Element;
  additionalToolbarButtons?: JSX.Element;
  visibleToolbarShortcuts?: ShortCuts[];
  hiddenToolbarShortcuts?: ShortCuts[];
  className?: string;
  onChange: (value: CustomElement[]) => void;
  value: CustomElement[];
  editor: Editor;
  onExpand?: () => void;
  isExpanded?: boolean;
  readOnly?: boolean;
  mentionOptions?: MentionOption[];
};

const RichTextEditor = memo(
  ({
    visibleToolbarShortcuts = [],
    hiddenToolbarShortcuts = [],
    actionElement,
    additionalToolbarButtons,
    placeholder,
    className,
    onChange,
    value,
    editor,
    onBlur,
    onExpand,
    isExpanded,
    readOnly,
    mentionOptions,
  }: RichTextEditorProps) => {
    const { formatMessage } = useIntl();

    const {
      onValueChange: onMentionValueChange,
      onKeyDown: onMentionKeyDown,
      dropdown: mentionDropdown,
    } = useMentions(editor, mentionOptions ?? []);

    const renderElement = useCallback(
      (props: RenderElementProps) => <RenderElement {...props} />,
      [],
    );
    const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);
    const decorateCb = useCallback((entry: NodeEntry) => decorate(editor, entry), [editor]);

    const isEditorEmpty =
      editor.children.length === 1 &&
      !['numbered-list', 'bulleted-list'].includes(
        Element.isElement(editor.children[0]) ? editor.children[0].type : '',
      );

    return (
      <Slate
        editor={editor}
        initialValue={value}
        onChange={(newValue) => {
          onChange(newValue as CustomElement[]);
          handleTextReplacement(editor);
          onMentionValueChange();
        }}
      >
        <div className="relative flex flex-col h-full overflow-y-auto">
          <LongTextWrap
            className={twMerge(
              'flex-1 flex flex-col max-h-full min-h-[88px] overflow-y-auto',
              className,
            )}
          >
            <Editable
              className="flex-1 h-full w-full break-words outline-none"
              onBlur={() => {
                onBlur?.({ textJson: value });
              }}
              readOnly={readOnly}
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              decorate={decorateCb}
              onKeyDown={(event) => {
                if (onMentionKeyDown(event)) return;

                if (event.key === 'Enter' && !event.shiftKey) {
                  const [lineEntry] = Editor.nodes(editor, {
                    match: (n) => Element.isElement(n) && n.type === 'code-line',
                  });
                  if (lineEntry) {
                    const [, linePath] = lineEntry;
                    const blockPath = Path.parent(linePath);
                    const block = Node.get(editor, blockPath);
                    const isEmpty = Editor.string(editor, linePath) === '';
                    const isLast =
                      Element.isElement(block) &&
                      linePath[linePath.length - 1] === block.children.length - 1;

                    if (isEmpty && isLast) {
                      event.preventDefault();
                      const onlyLine = Element.isElement(block) && block.children.length === 1;
                      Transforms.removeNodes(editor, { at: onlyLine ? blockPath : linePath });
                      const at = onlyLine ? blockPath : Path.next(blockPath);
                      if (!Node.has(editor, at)) {
                        Transforms.insertNodes(
                          editor,
                          { type: 'paragraph', children: [{ text: '' }] } as CustomElement,
                          { at },
                        );
                      }
                      Transforms.select(editor, Editor.start(editor, at));
                      return;
                    }
                  }
                }

                const { selection } = editor;

                if (selection && Range.isCollapsed(selection)) {
                  const { nativeEvent } = event;
                  const [linkNode] = Editor.nodes(editor, {
                    match: (n) => Element.isElement(n) && n.type === 'link',
                  });

                  if (linkNode) {
                    if (isKeyHotkey('left', nativeEvent)) {
                      event.preventDefault();
                      Transforms.move(editor, { unit: 'offset', reverse: true });
                      return;
                    }
                    if (isKeyHotkey('right', nativeEvent)) {
                      event.preventDefault();
                      Transforms.move(editor, { unit: 'offset' });
                      return;
                    }
                  }

                  for (const hotkey of Object.keys(hotKeys) as Array<keyof typeof hotKeys>) {
                    if (isKeyHotkey(hotkey, event)) {
                      event.preventDefault();
                      const mark = hotKeys[hotkey] as Format;
                      toggleFormat(editor, mark);
                    }
                  }
                }
              }}
              placeholder={
                isEditorEmpty ? placeholder || formatMessage({ id: 'desk-app.write-message' }) : ''
              }
              spellCheck
            />

            {onExpand && (
              <div onClick={onExpand} className="absolute right-2 bottom-2 cursor-pointer">
                {isExpanded && <CloseFullScreen className="text-[24px] text-v2blueGray-500" />}
                {!isExpanded && <OpenInFull className="text-[24px] text-v2blueGray-500" />}
              </div>
            )}
          </LongTextWrap>
        </div>

        <Toolbar
          visibleToolbarShortcuts={visibleToolbarShortcuts}
          hiddenToolbarShortcuts={hiddenToolbarShortcuts}
          additionalToolbarButtons={additionalToolbarButtons}
          actionElement={actionElement}
          readOnly={readOnly}
        />

        {mentionDropdown}
      </Slate>
    );
  },
);

export default RichTextEditor;
